import { ReactNode, useEffect, useState } from "react";

import { useBasicData } from "@/hooks";
import { getEnv, getNextAppLoginUrl } from "@/utils/env";
import { capturePostHogEvent } from "@/utils/posthog";

const ROLLOUT_FLAG = "next-app-rollout";
const AUTO_REDIRECT_FLAG = "next-app-new-user-redirect";
const AUTO_REDIRECTED_KEY = "layer.nextAppAutoRedirected";

type PostHogFlags = Record<string, { enabled?: boolean } | undefined>;

let flagsPromise: Promise<PostHogFlags> | undefined;

// One PostHog evaluation per page load, shared by every consumer.
const fetchFlags = (userId: string): Promise<PostHogFlags> => {
  if (!flagsPromise) {
    const { posthogKey } = getEnv();
    flagsPromise = fetch("https://eu.i.posthog.com/flags?v=2", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ api_key: posthogKey, distinct_id: userId }),
    })
      .then((response) => response.json())
      .then((data) => data.flags ?? {})
      .catch(() => ({})); // PostHog unreachable (e.g. ad-blocker): treat as disabled
  }
  return flagsPromise;
};

/**
 * Whether the given PostHog flag is enabled for the current user. False until
 * resolved, false on error, false when there is no user id. Also false inside
 * an ENTERPRISE workspace (and while the workspace tier is unknown) — the
 * rollout is invisible there — except for Layer employees, who always follow
 * the flag.
 */
const useNextAppFlag = (flag: string): boolean => {
  const { user, activeWorkspace } = useBasicData();
  const userId = user?.id;
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let isActive = true;
    fetchFlags(userId).then((flags) => {
      if (isActive) setIsEnabled(flags[flag]?.enabled === true);
    });
    return () => {
      isActive = false;
    };
  }, [userId, flag]);

  const isEmployee = user?.email?.endsWith("@layer.ai") ?? false;
  const planTier = activeWorkspace?.planTier;

  return (
    isEnabled && (isEmployee || (planTier != null && planTier !== "ENTERPRISE"))
  );
};

/**
 * Whether the current user is in the PostHog `next-app-rollout` cohort.
 * Consumed by the visit tracker and by the switch-to-next-app UI (banner,
 * workspace menu).
 */
export const useNextAppRolloutEnabled = (): boolean =>
  useNextAppFlag(ROLLOUT_FLAG);

let isRedirectingToNextApp = false;

/**
 * One-time redirect for users in the PostHog `next-app-new-user-redirect`
 * cohort (a % of new signups): their first legacy page load sends them to the
 * next app, through its login endpoint so the shared Auth0 session logs them
 * in silently. The redirect fires at most once per browser (localStorage) —
 * after it, the banner and the workspace dropdown remain as the ways between
 * the apps.
 */
export const NextAppGate = (): ReactNode => {
  const isRedirectEnabled = useNextAppFlag(AUTO_REDIRECT_FLAG);

  useEffect(() => {
    if (!isRedirectEnabled) return;
    if (localStorage.getItem(AUTO_REDIRECTED_KEY)) return;
    localStorage.setItem(AUTO_REDIRECTED_KEY, "1");
    isRedirectingToNextApp = true;
    window.location.replace(getNextAppLoginUrl());
  }, [isRedirectEnabled]);

  return null;
};

let isVisitTracked = false;

/**
 * Emits one event per page load when a user in the `next-app-rollout` cohort
 * lands on the legacy app, so continued legacy usage can be measured against
 * next-app uptake.
 */
export const NextAppVisitTracker = (): ReactNode => {
  const { user } = useBasicData();
  const isRolloutEnabled = useNextAppRolloutEnabled();
  const userId = user?.id;

  useEffect(() => {
    if (!isRolloutEnabled || !userId || isVisitTracked) return;
    if (isRedirectingToNextApp) return;
    isVisitTracked = true;
    capturePostHogEvent(
      "legacy_app.visited",
      userId,
      document.referrer ? { referrer: document.referrer } : undefined,
    );
  }, [isRolloutEnabled, userId]);

  return null;
};
